/**
 * Severity helpers — clasificacion de urgencia por dias hasta evento.
 *
 * Sprint UX C panel Contabilidad. Reusado en AriaInbox + EstadoEjercicioCard
 * para ordenar items y aplicar badge color consistente.
 *
 * Umbrales calibrados para contexto contable espanol:
 *  - critical: <= 7 dias (decisiones inmediatas — vencimientos AEAT inminentes,
 *    descuadres bloqueantes)
 *  - high:     <= 30 dias (atencion esta semana — modelos del proximo mes)
 *  - medium:   <= 90 dias (planificar — vencimientos del proximo trimestre)
 *  - low:      > 90 dias o null (informativo)
 *
 * NOTA: el orden de severidad se usa en ARRAYS como prioridad — critical (0)
 * va primero, low (3) ultimo. Para SORT estable usar SEVERITY_ORDER.
 */

export type Severity = 'critical' | 'high' | 'medium' | 'low';

/** Orden de prioridad — critical primero, low ultimo. */
export const SEVERITY_ORDER: Record<Severity, number> = {
  critical: 0,
  high:     1,
  medium:   2,
  low:      3,
};

/**
 * Clases del badge por severidad. Sobre tokens: cada uno trae su variante oscura,
 * asi que ya no hay bloques `dark:` duplicando la tabla.
 *
 * La rampa tenia cuatro escalones (rojo, naranja, ambar, gris) y hay tres tokens
 * "calientes". `medium` baja a `info` en vez de compartir `warning` con `high`:
 * su umbral son 31-90 dias —planificar el proximo trimestre—, que es
 * informativo, no un aviso. El resultado sigue leyendose como gradiente de
 * urgencia: rojo, ambar, azul, gris. Es el unico cambio de TONO de este fichero.
 *
 * `orange` no tenia token y no lo tiene: la guia lo clasifica como decorativo.
 */
export const SEVERITY_BADGE_CLASS: Record<Severity, string> = {
  critical: 'bg-destructive/10 text-destructive border-destructive/30',
  high:     'bg-warning/10 text-warning border-warning/30',
  medium:   'bg-info/10 text-info border-info/30',
  low:      'bg-muted text-muted-foreground border-border',
};

/** Etiqueta humana corta de severity (es-ES). */
export const SEVERITY_LABEL: Record<Severity, string> = {
  critical: 'Critico',
  high:     'Alto',
  medium:   'Medio',
  low:      'Bajo',
};

/**
 * Mapea daysUntil (de NextDeadline.daysUntil o similar) a Severity.
 *
 * - daysUntil < 0  : critical (vencido — accion inmediata)
 * - daysUntil 0-7  : critical (esta semana)
 * - daysUntil 8-30 : high
 * - daysUntil 31-90: medium
 * - daysUntil > 90 : low
 * - null/undefined : low
 */
export function severityFromDays(daysUntil: number | null | undefined): Severity {
  if (daysUntil == null) return 'low';
  if (daysUntil <= 7) return 'critical';   // incluye negativos (vencidos) y proximos 7d
  if (daysUntil <= 30) return 'high';
  if (daysUntil <= 90) return 'medium';
  return 'low';
}

/**
 * Compara 2 severities para sort estable. Returns negative si a < b (a primero).
 */
export function compareSeverity(a: Severity, b: Severity): number {
  return SEVERITY_ORDER[a] - SEVERITY_ORDER[b];
}

/**
 * Mapea backend severity legacy ('warning', 'info') a nuestro tipo unificado.
 * Backend aria/inbox.ts emite 'critical' | 'warning' | 'info' historicamente —
 * este helper normaliza a 'critical' | 'high' | 'medium' | 'low' para que el
 * frontend tenga UN solo vocabulario de severity.
 *
 * Mapping:
 *  - 'critical' -> 'critical'
 *  - 'warning'  -> 'high'   (warnings backend son items que requieren atencion pronto)
 *  - 'info'     -> 'medium' (info backend = nice-to-know pero accionable)
 *  - desconocido -> 'low'
 */
export function normalizeSeverity(raw: string | null | undefined): Severity {
  switch (raw) {
    case 'critical': return 'critical';
    case 'warning':  return 'high';
    case 'info':     return 'medium';
    case 'high':     return 'high';
    case 'medium':   return 'medium';
    case 'low':      return 'low';
    default:         return 'low';
  }
}
